import React, { useState, useEffect } from 'react';
import { safeAsync, Badge, KPI, neuShadow, neuShadowSm, neuShadowInset, inp, btnPrimary, btnSm, btnDanger, cardWrap, emptyMsg, overlayStyle, modalStyle, modalHeader, modalTitle, closeBtn } from './crmShared';
const api = () => window.zignagenixAPI;

export default function SocialPlannerTab({ canCreate, canUpdate }) {
  const [activeTab, setActiveTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [triggerLinks, setTriggerLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postForm, setPostForm] = useState({ content: '', platforms: [], scheduleDate: '', scheduleTime: '', mediaUrls: [] });
  const [linkForm, setLinkForm] = useState({ name: '', redirectUrl: '', actions: [] });
  const [accountForm, setAccountForm] = useState({ platform: '', authData: '' });
  const [aiGenerating, setAiGenerating] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    /*
     * ⚠⚠ safeAsync(fn) RETURNS A FUNCTION — it has to be CALLED. `await
     * safeAsync(fn)` awaits the wrapper itself, which is not a promise, so the
     * body NEVER RUNS and the API call is never made. Card P71 found three
     * layers stacked here: the wrapper was never invoked, the channel it would
     * have called does not exist, and an error body would not have surfaced.
     * The trailing () is the first layer.
     */
    await safeAsync(async () => {
      // Defensive: coerce truthy non-arrays to [] so later .filter() / .map()
      // calls don't crash with "x.filter is not a function".
      if (activeTab === 'posts') {
        const data = await api().getCRMSocialPosts({});
        setPosts(Array.isArray(data) ? data : []);
      } else if (activeTab === 'accounts') {
        const data = await api().getCRMSocialAccounts();
        setAccounts(Array.isArray(data) ? data : []);
      } else if (activeTab === 'trigger-links') {
        const data = await api().getCRMTriggerLinks();
        setTriggerLinks(Array.isArray(data) ? data : []);
      }
    })();
    setLoading(false);
  };

  const handleCreatePost = async () => {
    if (!postForm.content || postForm.platforms.length === 0) return;
    // ⚠ The API call is the safeAsync BODY and the wrapper is INVOKED — both
    // are required for an { error: true } reply to reach the user. See the
    // note in loadData.
    const result = await safeAsync(() => (editingPost
      ? api().updateCRMSocialPost(editingPost.id, postForm)
      : api().createCRMSocialPost(postForm)))();
    if (result) {
      setShowPostModal(false);
      setPostForm({ content: '', platforms: [], scheduleDate: '', scheduleTime: '', mediaUrls: [] });
      setEditingPost(null);
      loadData();
    }
  };

  /*
   * ⛔ THIS MARKS. IT DOES NOT POST. crm:social:publish-post
   * (routes/crm.js:12665) runs one UPDATE — status='published',
   * published_at=NOW() — and writes an audit row. Nothing reaches Instagram or
   * Facebook; there is no social API client anywhere in the tree.
   */
  const handleMarkPublished = async (postId) => {
    /*
     * ⚠ THE API CALL IS THE safeAsync BODY, not something buried inside it.
     * safeAsync inspects what its callback RETURNS; a callback that awaits the
     * API and then discards the value hands back undefined, and an
     * { error: true } body is swallowed exactly as it was before this card.
     * Returning the call directly is what lets the error reach the user — and
     * `if (result)` is then the success branch, because safeAsync returns
     * undefined for both a throw and an error body.
     */
    const result = await safeAsync(() => api().publishCRMSocialPost(postId))();
    if (result) loadData();
  };

  // ⛔ UNREACHABLE BY DESIGN — see the disabled control in the post list.
  // eslint-disable-next-line no-unused-vars
  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    const result = await safeAsync(() => api().deleteCRMSocialPost(postId))();
    if (result) loadData();
  };

  // ⛔ UNREACHABLE BY DESIGN — the control that called this is disabled above.
  // Left in place rather than deleted so the P75 recon can see what it was
  // meant to do; deleting it would hide the requirement.
  // eslint-disable-next-line no-unused-vars
  const handleAIGeneratePost = async () => {
    setAiGenerating(true);
    await safeAsync(async () => {
      const result = await api().sendCRMAIChat({
        context: 'Generate an engaging social media post for our CRM business. Make it professional yet conversational.',
        messages: [{ role: 'user', content: 'Generate a social media post' }]
      });
      if (result?.content) {
        setPostForm({ ...postForm, content: result.content });
      }
    });
    setAiGenerating(false);
  };

  // ⛔ UNREACHABLE BY DESIGN — see the disabled controls below.
  // eslint-disable-next-line no-unused-vars
  const handleConnectAccount = async () => {
    if (!accountForm.platform) return;
    /*
     * ⚠ "Connect" RECORDS A ROW. crm:social:connect-account runs one INSERT
     * into crm_social_accounts and reaches no platform — there is no OAuth
     * flow and no social API client anywhere in the tree. The banner at the
     * top of the tab already says posting is not connected; this control
     * stores the fact that an account exists, nothing more.
     */
    const result = await safeAsync(() => api().connectCRMSocialAccount(accountForm))();
    if (result) {
      setShowAccountModal(false);
      setAccountForm({ platform: '', authData: '' });
      loadData();
    }
  };

  const handleDisconnectAccount = async (accountId) => {
    if (!window.confirm('Disconnect this account?')) return;
    const result = await safeAsync(() => api().disconnectCRMSocialAccount(accountId))();
    if (result) loadData();
  };

  // ⛔ UNREACHABLE BY DESIGN — see the disabled control below.
  // eslint-disable-next-line no-unused-vars
  const handleCreateLink = async () => {
    if (!linkForm.name || !linkForm.redirectUrl) return;
    await safeAsync(async () => {
      await api().createCRMTriggerLink(linkForm);
      setShowLinkModal(false);
      setLinkForm({ name: '', redirectUrl: '', actions: [] });
      loadData();
    });
  };

  const platformConfig = {
    facebook: { color: '#1877F2', icon: 'f' },
    instagram: { color: '#E4405F', icon: 'i' },
    linkedin: { color: '#0A66C2', icon: 'in' },
    twitter: { color: '#000000', icon: 'x' }
  };

  const kpis = [
    { label: 'Total Posts', value: posts.length },
    { label: 'Scheduled', value: posts.filter(p => p.status === 'scheduled').length },
    { label: 'Published', value: posts.filter(p => p.status === 'published' && new Date(p.createdAt).getMonth() === new Date().getMonth()).length },
    { label: 'Engagement', value: posts.reduce((sum, p) => sum + (p.stats?.likes || 0) + (p.stats?.comments || 0) + (p.stats?.shares || 0), 0) }
  ];

  return (
    <div style={{ padding: '20px' }}>
      {/* Card P71 — the boundary, stated once at the top of the tab. */}
      <div
        data-testid="social-not-connected-banner"
        style={{
          background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E',
          borderRadius: 6, padding: '8px 10px', fontSize: 12, marginBottom: 16,
        }}
      >
        Posting to Instagram/Facebook is not connected yet. <strong>Mark as published</strong>{' '}
        records the status in the CRM only — nothing is posted. Post from GoHighLevel.
      </div>
      <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', borderBottom: '1px solid #ddd' }}>
        {['posts', 'accounts', 'trigger-links'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 15px',
              border: 'none',
              background: activeTab === tab ? '#6C63FF' : 'transparent',
              color: activeTab === tab ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: 'bold',
              borderBottom: activeTab === tab ? '3px solid #6C63FF' : 'none'
            }}
          >
            {tab === 'posts' ? 'Posts' : tab === 'accounts' ? 'Accounts' : 'Trigger Links'}
          </button>
        ))}
      </div>

      {activeTab === 'posts' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '30px' }}>
            {kpis.map(kpi => (
              <KPI key={kpi.label} label={kpi.label} value={kpi.value} />
            ))}
          </div>

          {canCreate && (
            <button onClick={() => { setEditingPost(null); setPostForm({ content: '', platforms: [], scheduleDate: '', scheduleTime: '', mediaUrls: [] }); setShowPostModal(true); }} style={btnPrimary}>
              + New Post
            </button>
          )}

          <div style={{ marginTop: '20px' }}>
            {loading ? (
              <div style={emptyMsg}>Loading posts...</div>
            ) : posts.length === 0 ? (
              <div style={emptyMsg}>No posts yet. Create one to get started.</div>
            ) : (
              posts.map(post => (
                <div key={post.id} style={{ ...cardWrap, marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: '10px' }}>
                        {post.platforms?.map(p => (
                          <Badge key={p} label={p.toUpperCase()} style={{ marginRight: '5px', backgroundColor: platformConfig[p]?.color || '#666' }} />
                        ))}
                        <Badge label={post.status} style={{ backgroundColor: post.status === 'published' ? '#10b981' : post.status === 'scheduled' ? '#f59e0b' : '#6b7280' }} />
                      </div>
                      <p style={{ marginBottom: '10px', color: '#333' }}>{post.content}</p>
                      {post.scheduleDate && <p style={{ fontSize: '12px', color: '#999' }}>Scheduled: {post.scheduleDate} {post.scheduleTime}</p>}
                      {post.stats && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                          👍 {post.stats.likes} | 💬 {post.stats.comments} | ↗️ {post.stats.shares}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      {canUpdate && post.status === 'draft' && (
                        <>
                          <button onClick={() => { setEditingPost(post); setPostForm({ content: post.content, platforms: post.platforms || [], scheduleDate: post.scheduleDate || '', scheduleTime: post.scheduleTime || '', mediaUrls: post.mediaUrls || [] }); setShowPostModal(true); }} style={btnSm}>Edit</button>
                          <button
                            onClick={() => handleMarkPublished(post.id)}
                            style={btnSm}
                            title="Mark as published — records it in the CRM. Nothing is posted."
                          >
                            Mark as published
                          </button>
                        </>
                      )}
                      {/*
                        ⛔⛔ DISABLED — HARD DELETE (review round 2).
                        crm:social:delete-post runs an unconditional
                        DELETE FROM crm_social_posts with no archive. Same rule
                        as the invoice delete: inward-only is not the same as
                        safe to switch on from a button that was dead until
                        yesterday.
                      */}
                      {canUpdate && (
                        <button
                          onClick={undefined}
                          disabled
                          title="Not available yet — deleting a post is permanent and is not done from this screen."
                          style={{ ...btnDanger, opacity: 0.5, cursor: 'not-allowed' }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {showPostModal && (
            <div style={overlayStyle} onClick={() => setShowPostModal(false)}>
              <div style={modalStyle} onClick={e => e.stopPropagation()}>
                <div style={modalHeader}>
                  <h2 style={modalTitle}>{editingPost ? 'Edit Post' : 'Create Social Post'}</h2>
                  <button onClick={() => setShowPostModal(false)} style={closeBtn}>✕</button>
                </div>
                <div style={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto' }}>
                  <label style={{ display: 'block', marginBottom: '15px' }}>
                    <strong>Content</strong>
                    <textarea
                      value={postForm.content}
                      onChange={e => setPostForm({ ...postForm, content: e.target.value })}
                      style={{ ...inp, minHeight: '120px', marginTop: '5px' }}
                      placeholder="What's on your mind?"
                    />
                  </label>

                  <label style={{ display: 'block', marginBottom: '15px' }}>
                    <strong>Platforms</strong>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginTop: '5px' }}>
                      {Object.keys(platformConfig).map(p => (
                        <label key={p} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            checked={postForm.platforms.includes(p)}
                            onChange={e => setPostForm({
                              ...postForm,
                              platforms: e.target.checked ? [...postForm.platforms, p] : postForm.platforms.filter(x => x !== p)
                            })}
                          />
                          {p.charAt(0).toUpperCase() + p.slice(1)}
                        </label>
                      ))}
                    </div>
                  </label>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                    <label style={{ display: 'block' }}>
                      <strong>Schedule Date</strong>
                      <input
                        type="date"
                        value={postForm.scheduleDate}
                        onChange={e => setPostForm({ ...postForm, scheduleDate: e.target.value })}
                        style={{ ...inp, marginTop: '5px' }}
                      />
                    </label>
                    <label style={{ display: 'block' }}>
                      <strong>Time</strong>
                      <input
                        type="time"
                        value={postForm.scheduleTime}
                        onChange={e => setPostForm({ ...postForm, scheduleTime: e.target.value })}
                        style={{ ...inp, marginTop: '5px' }}
                      />
                    </label>
                  </div>

                  <label style={{ display: 'block', marginBottom: '15px' }}>
                    <strong>Media URLs</strong>
                    <input
                      type="text"
                      value={postForm.mediaUrls.join(', ')}
                      onChange={e => setPostForm({ ...postForm, mediaUrls: e.target.value.split(',').map(u => u.trim()).filter(u => u) })}
                      style={{ ...inp, marginTop: '5px' }}
                      placeholder="URLs separated by commas"
                    />
                  </label>

                  {/*
                    ⛔ DISABLED, TWO INDEPENDENT REASONS (card P71a).
                    1. `sendCRMAIChat` points at crm:entity:action, which is
                       registered nowhere — it has never produced a caption.
                    2. The only real AI path (routes/crm_ai.js) calls Bedrock
                       via _ai_shared, which is OUTWARD. The ruling's boundary
                       forbids wiring an outward sender from these tabs, so it
                       is greyed with a reason rather than re-pointed.
                  */}
                  <button
                    onClick={undefined}
                    disabled
                    title="Not available yet — AI drafting is not connected from this tab."
                    style={{ ...btnSm, marginBottom: '15px', width: '100%', opacity: 0.5, cursor: 'not-allowed' }}
                  >
                    {'✨ AI Generate Post — Not available yet'}
                  </button>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button onClick={() => setShowPostModal(false)} style={btnSm}>Cancel</button>
                    <button onClick={handleCreatePost} style={btnPrimary}>Save Post</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'accounts' && (
        <>
          {/*
            ⛔⛔ DISABLED — IT WOULD INVITE A PLAINTEXT OAUTH TOKEN
              (review round 2). crm:social:connect-account INSERTs
              `access_token` into crm_social_accounts as plain text, and that
              column is in NEITHER database/encryptedColumns.js NOR
              database/plaintextColumnAllowlist.js — it has never been
              classified at all. Wiring this form asks staff to paste a
              long-lived Instagram/Facebook token into a column nobody has
              reviewed. Inward-only, and emphatically not safe.
            ⚠ Disconnect stays wired: it only sets is_active = FALSE.
          */}
          {canCreate && (
            <button onClick={undefined} disabled title="Not available yet — connecting an account is not available from this screen." style={{ ...btnPrimary, opacity: 0.5, cursor: 'not-allowed' }}>
              + Connect Account
            </button>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '15px', marginTop: '20px' }}>
            {loading ? (
              <div style={emptyMsg}>Loading accounts...</div>
            ) : accounts.length === 0 ? (
              <div style={emptyMsg}>No connected accounts yet.</div>
            ) : (
              accounts.map(account => (
                <div key={account.id} style={{ ...cardWrap, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: '24px', marginBottom: '10px', color: platformConfig[account.platform]?.color || '#666' }}>
                    {platformConfig[account.platform]?.icon}
                  </div>
                  <h3 style={{ margin: '0 0 5px 0' }}>{account.accountName}</h3>
                  <p style={{ margin: '0 0 10px 0', color: '#666', fontSize: '12px' }}>{account.platform.toUpperCase()}</p>
                  <Badge label={account.status} style={{ backgroundColor: account.status === 'active' ? '#10b981' : '#ef4444', marginBottom: '15px' }} />
                  {canUpdate && (
                    <button onClick={() => handleDisconnectAccount(account.id)} style={btnDanger}>
                      Disconnect
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {showAccountModal && (
            <div style={overlayStyle} onClick={() => setShowAccountModal(false)}>
              <div style={modalStyle} onClick={e => e.stopPropagation()}>
                <div style={modalHeader}>
                  <h2 style={modalTitle}>Connect Social Account</h2>
                  <button onClick={() => setShowAccountModal(false)} style={closeBtn}>✕</button>
                </div>
                <div style={{ padding: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '15px' }}>
                    <strong>Platform</strong>
                    <select
                      value={accountForm.platform}
                      onChange={e => setAccountForm({ ...accountForm, platform: e.target.value })}
                      style={{ ...inp, marginTop: '5px' }}
                    >
                      <option value="">Select platform...</option>
                      {Object.keys(platformConfig).map(p => (
                        <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                      ))}
                    </select>
                  </label>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button onClick={() => setShowAccountModal(false)} style={btnSm}>Cancel</button>
                    <button
                      onClick={undefined}
                      disabled
                      title="Not available yet — connecting an account is not available from this screen."
                      style={{ ...btnPrimary, opacity: 0.5, cursor: 'not-allowed' }}
                    >
                      Connect — Not available yet
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'trigger-links' && (
        <>
          {canCreate && (
            <button onClick={() => { setLinkForm({ name: '', redirectUrl: '', actions: [] }); setShowLinkModal(true); }} style={btnPrimary}>
              + New Link
            </button>
          )}

          <div style={{ marginTop: '20px' }}>
            {loading ? (
              <div style={emptyMsg}>Loading trigger links...</div>
            ) : triggerLinks.length === 0 ? (
              <div style={emptyMsg}>No trigger links yet.</div>
            ) : (
              triggerLinks.map(link => (
                <div key={link.id} style={{ ...cardWrap, marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 5px 0' }}>{link.name}</h4>
                      <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#666', wordBreak: 'break-all' }}>{link.url}</p>
                      <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: '#666' }}>Redirects to: {link.redirectUrl}</p>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                        Clicks: {link.totalClicks} | Unique: {link.uniqueClicks}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {showLinkModal && (
            <div style={overlayStyle} onClick={() => setShowLinkModal(false)}>
              <div style={modalStyle} onClick={e => e.stopPropagation()}>
                <div style={modalHeader}>
                  <h2 style={modalTitle}>Create Trigger Link</h2>
                  <button onClick={() => setShowLinkModal(false)} style={closeBtn}>✕</button>
                </div>
                <div style={{ padding: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '15px' }}>
                    <strong>Link Name</strong>
                    <input
                      type="text"
                      value={linkForm.name}
                      onChange={e => setLinkForm({ ...linkForm, name: e.target.value })}
                      style={{ ...inp, marginTop: '5px' }}
                      placeholder="e.g., Campaign Q2"
                    />
                  </label>

                  <label style={{ display: 'block', marginBottom: '15px' }}>
                    <strong>Redirect URL</strong>
                    <input
                      type="url"
                      value={linkForm.redirectUrl}
                      onChange={e => setLinkForm({ ...linkForm, redirectUrl: e.target.value })}
                      style={{ ...inp, marginTop: '5px' }}
                      placeholder="https://..."
                    />
                  </label>

                  <label style={{ display: 'block', marginBottom: '15px' }}>
                    <strong>Actions on Click</strong>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input
                          type="checkbox"
                          checked={linkForm.actions.includes('add_tag')}
                          onChange={e => setLinkForm({
                            ...linkForm,
                            actions: e.target.checked ? [...linkForm.actions, 'add_tag'] : linkForm.actions.filter(a => a !== 'add_tag')
                          })}
                        />
                        Add Tag
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input
                          type="checkbox"
                          checked={linkForm.actions.includes('move_stage')}
                          onChange={e => setLinkForm({
                            ...linkForm,
                            actions: e.target.checked ? [...linkForm.actions, 'move_stage'] : linkForm.actions.filter(a => a !== 'move_stage')
                          })}
                        />
                        Move Stage
                      </label>
                    </div>
                  </label>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button onClick={() => setShowLinkModal(false)} style={btnSm}>Cancel</button>
                    {/*
                      ⛔ DISABLED — SHAPE MISMATCH, not a missing handler.
                      crm:social:create-trigger-link exists and is inward-only,
                      but it INSERTs (post_id, url, label) while this form
                      collects { name, redirectUrl, actions }. Re-pointing it
                      would write three NULLs and report success — a worse
                      outcome than the silent button, because a row would
                      appear. Wiring it needs the form and the handler
                      reconciled, which is a decision, not a rename.
                    */}
                    <button
                      onClick={undefined}
                      disabled
                      title="Not available yet — the link form and the server do not agree on what a trigger link is."
                      style={{ ...btnPrimary, opacity: 0.5, cursor: 'not-allowed' }}
                    >
                      Create Link — Not available yet
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
